"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Contraction map (module level) — lowercase contraction → full     */
/*  form. Ambiguous entries ('s = is/has, 'd = would/had) expand to   */
/*  the most common form and are flagged for review.                  */
/* ------------------------------------------------------------------ */

const CONTRACTIONS: Record<string, string> = {
  "aren't": "are not",
  "can't": "cannot",
  "couldn't": "could not",
  "daren't": "dare not",
  "didn't": "did not",
  "doesn't": "does not",
  "don't": "do not",
  "hadn't": "had not",
  "hasn't": "has not",
  "haven't": "have not",
  "isn't": "is not",
  "mightn't": "might not",
  "mustn't": "must not",
  "needn't": "need not",
  "oughtn't": "ought not",
  "shan't": "shall not",
  "shouldn't": "should not",
  "wasn't": "was not",
  "weren't": "were not",
  "won't": "will not",
  "wouldn't": "would not",
  "ain't": "am not",
  "i'm": "i am",
  "i've": "i have",
  "i'll": "i will",
  "i'd": "i would",
  "you're": "you are",
  "you've": "you have",
  "you'll": "you will",
  "you'd": "you would",
  "he's": "he is",
  "he'll": "he will",
  "he'd": "he would",
  "she's": "she is",
  "she'll": "she will",
  "she'd": "she would",
  "it's": "it is",
  "it'll": "it will",
  "it'd": "it would",
  "we're": "we are",
  "we've": "we have",
  "we'll": "we will",
  "we'd": "we would",
  "they're": "they are",
  "they've": "they have",
  "they'll": "they will",
  "they'd": "they would",
  "that's": "that is",
  "that'll": "that will",
  "that'd": "that would",
  "there's": "there is",
  "there'll": "there will",
  "there'd": "there would",
  "here's": "here is",
  "what's": "what is",
  "what'll": "what will",
  "what've": "what have",
  "who's": "who is",
  "who'll": "who will",
  "who'd": "who would",
  "who've": "who have",
  "where's": "where is",
  "where'd": "where did",
  "when's": "when is",
  "why's": "why is",
  "why'd": "why did",
  "how's": "how is",
  "how'd": "how did",
  "let's": "let us",
  "would've": "would have",
  "could've": "could have",
  "should've": "should have",
  "might've": "might have",
  "must've": "must have",
  "y'all": "you all",
  "ma'am": "madam",
  "everyone's": "everyone is",
  "everybody's": "everybody is",
};

const AMBIGUOUS_NOTES: Record<string, string> = {
  "ain't": "could be 'am not', 'is not', 'are not', 'has not', or 'have not'",
  "i'd": "could be 'I would' or 'I had'",
  "you'd": "could be 'you would' or 'you had'",
  "he's": "could be 'he is' or 'he has'",
  "he'd": "could be 'he would' or 'he had'",
  "she's": "could be 'she is' or 'she has'",
  "she'd": "could be 'she would' or 'she had'",
  "it's": "could be 'it is' or 'it has'",
  "it'd": "could be 'it would' or 'it had'",
  "we'd": "could be 'we would' or 'we had'",
  "they'd": "could be 'they would' or 'they had'",
  "that's": "could be 'that is' or 'that has'",
  "that'd": "could be 'that would' or 'that had'",
  "there's": "could be 'there is' or 'there has'",
  "there'd": "could be 'there would' or 'there had'",
  "here's": "could be 'here is' or 'here has'",
  "what's": "could be 'what is' or 'what has'",
  "who's": "could be 'who is' or 'who has'",
  "who'd": "could be 'who would' or 'who had'",
  "where's": "could be 'where is' or 'where has'",
  "where'd": "could be 'where did' or 'where would'",
  "when's": "could be 'when is' or 'when has'",
  "why's": "could be 'why is' or 'why has'",
  "why'd": "could be 'why did' or 'why would'",
  "how's": "could be 'how is' or 'how has'",
  "how'd": "could be 'how did' or 'how would'",
  "everyone's": "could be 'everyone is' or 'everyone has'",
  "everybody's": "could be 'everybody is' or 'everybody has'",
};

const RE_CONTRACTION = new RegExp(
  `\\b(?:${Object.keys(CONTRACTIONS)
    .sort((a, b) => b.length - a.length)
    .join("|")
    .replace(/'/g, "['’]")})\\b`,
  "gi"
);
const RE_CURLY_APOSTROPHE = /’/g;
const RE_UPPERCASE = /[A-Z]/;
/** The word after a contraction, skipping one adverb ("it's never been"). */
const RE_NEXT_WORD =
  /^\s+(?:(?:never|already|just|always|also|still|not|ever|really|finally|actually|only)\s+)?([a-z]+)/i;
/** After 's these can only follow "has": "it's been", "he's got". */
const HAS_PARTICIPLES = new Set([
  "been",
  "got",
  "gotten",
  "had",
  "become",
  "begun",
  "come",
]);
/** After 'd these can only follow "had" ("she'd gone"; "would gone" is not English). */
const HAD_PARTICIPLES = new Set([
  "been",
  "gone",
  "done",
  "seen",
  "taken",
  "made",
  "had",
  "got",
  "gotten",
  "become",
  "begun",
  "better",
]);
/** Contractions whose 'd always means "did", never "had". */
const RE_TRAILING_IS = / is$/;
const RE_TRAILING_WOULD = / would$/;
const DID_ONLY = new Set(["where'd", "why'd", "how'd"]);

/* ------------------------------------------------------------------ */
/*  Expansion                                                         */
/* ------------------------------------------------------------------ */

interface AmbiguousFinding {
  contraction: string;
  expandedTo: string;
  note: string;
  count: number;
}

export interface ExpansionResult {
  output: string;
  replaced: number;
  ambiguous: AmbiguousFinding[];
}

function matchCapitalization(original: string, expansion: string): string {
  if (RE_UPPERCASE.test(original.charAt(0))) {
    return expansion.charAt(0).toUpperCase() + expansion.slice(1);
  }
  return expansion;
}

function fixStandaloneI(expansion: string): string {
  // The pronoun "I" is always capitalized, even mid-sentence.
  if (expansion.startsWith("i ")) {
    return `I ${expansion.slice(2)}`;
  }
  return expansion;
}

/**
 * Pick "has"/"had" over the default "is"/"would" when the next word is a past
 * participle. Returns null when the default expansion stands.
 */
export function resolveAuxiliary(
  key: string,
  expansion: string,
  following: string
): string | null {
  const next = RE_NEXT_WORD.exec(following)?.[1]?.toLowerCase();
  if (!next) {
    return null;
  }
  if (key.endsWith("'s") && key !== "let's" && HAS_PARTICIPLES.has(next)) {
    return expansion.replace(RE_TRAILING_IS, " has");
  }
  if (key.endsWith("'d") && !DID_ONLY.has(key) && HAD_PARTICIPLES.has(next)) {
    return expansion.replace(RE_TRAILING_WOULD, " had");
  }
  return null;
}

export function expandContractions(text: string): ExpansionResult {
  const ambiguousCounts = new Map<string, number>();
  let replaced = 0;
  const output = text.replace(RE_CONTRACTION, (match, offset: number) => {
    const key = match.toLowerCase().replace(RE_CURLY_APOSTROPHE, "'");
    const expansion = CONTRACTIONS[key];
    if (!expansion) {
      return match;
    }
    replaced += 1;
    const resolved = resolveAuxiliary(
      key,
      expansion,
      text.slice(offset + match.length)
    );
    if (resolved === null && AMBIGUOUS_NOTES[key]) {
      ambiguousCounts.set(key, (ambiguousCounts.get(key) ?? 0) + 1);
    }
    return matchCapitalization(match, fixStandaloneI(resolved ?? expansion));
  });

  const ambiguous: AmbiguousFinding[] = Array.from(ambiguousCounts.entries())
    .map(([contraction, count]) => ({
      contraction,
      expandedTo: fixStandaloneI(CONTRACTIONS[contraction] ?? ""),
      note: AMBIGUOUS_NOTES[contraction] ?? "",
      count,
    }))
    .sort((a, b) => b.count - a.count);

  return { output, replaced, ambiguous };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const CONTRACTION_EXPANDER_STRINGS = {
  textWithContractions: "Text with contractions",
  pasteYourTextHereDon:
    "Paste your text here. Don't worry — it's expanded instantly and we'll flag anything ambiguous.",
  expandContractions: "Expand Contractions",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  expandedText: "Expanded text",
  contractionsExpanded: "Contractions Expanded",
  ambiguousToCheck: "Ambiguous to Check",
  ambiguousCheckThese: "Ambiguous — Check These",
  theseContractionsHaveMoreThan:
    "These contractions have more than one possible full form. Each was expanded to the most common reading — verify it matches your intended meaning in context.",
  occurrence: "one: occurrence|other: occurrences",
  noContractionsFound: "No contractions found",
  noneOfTheContractionsOn:
    "None of the contractions on our list appear in your text — it is already in full formal form.",
} as const;

export default function ContractionExpanderWidget() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState<ExpansionResult | null>(null);
  const [copied, setCopied] = useState(false);

  function handleExpand() {
    if (input.trim().length === 0) {
      return;
    }
    setResult(expandContractions(input));
    setCopied(false);
  }

  function handleCopy() {
    if (!result) {
      return;
    }
    writeToClipboard(result.output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setResult(null);
    setCopied(false);
  }

  const locale = useToolLocale();
  const strings = useToolUi(CONTRACTION_EXPANDER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="contraction-input"
        >
          {strings.textWithContractions}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="contraction-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.pasteYourTextHereDon}
          value={input}
        />
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleExpand}
          type="button"
        >
          {strings.expandContractions}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={!result}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && !result}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {result && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="contraction-output"
          >
            {strings.expandedText}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="contraction-output"
            readOnly
            value={result.output}
          />
        </div>
      )}
      {/* Stats */}
      {result && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            label={strings.contractionsExpanded}
            value={fmt(result.replaced)}
          />
          <StatCard
            label={strings.ambiguousToCheck}
            value={result.ambiguous.reduce((sum, a) => sum + a.count, 0)}
          />
        </div>
      )}
      {/* Ambiguous list */}
      {result && result.ambiguous.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.ambiguousCheckThese}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.theseContractionsHaveMoreThan}
          </p>
          <div className="space-y-3">
            {result.ambiguous.map((finding) => (
              <div
                className="rounded-lg border border-border bg-card p-4"
                key={finding.contraction}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground text-sm">
                    {finding.contraction} → {finding.expandedTo}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {fmt(finding.count)}
                    {tPlural(strings.occurrence, locale, finding.count)}
                  </span>
                </div>
                <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                  {finding.note}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* No contractions found */}
      {result && result.replaced === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noContractionsFound}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.noneOfTheContractionsOn}
          </p>
        </div>
      )}
    </>
  );
}
